/**
 * České UI řetězce (jazyk dle /Design). Centralizované kvůli budoucí lokalizaci.
 */
import type { ClaudeModelId } from "@app-factory/shared";

/** Popisky Claude modelů pro selecty (modelHint oddělení a routin). */
const claudeModelLabels: Record<ClaudeModelId, string> = {
  "claude-fable-5": "Fable 5 — nejvyšší inteligence, nejdražší",
  "claude-opus-5-5": "Opus 5.5 — nejlepší na programování",
  "claude-opus-5": "Opus 5 — starší Opus",
  "claude-sonnet-5": "Sonnet 5 — vyvážený výkon a cena",
  "claude-sonnet-4-6": "Sonnet 4.6 — starší Sonnet",
  "claude-haiku-4-5": "Haiku 4.5 — nejrychlejší a nejlevnější",
};

export const cs = {
  models: claudeModelLabels,
  app: {
    name: "APP FACTORY",
    claim: "DIGITAL COMPANY OS",
  },
  nav: {
    command: "COMMAND CENTER",
    projects: "PROJEKTY",
    plan: "PLÁN",
    decisions: "ROZHODNUTÍ",
    humanTasks: "ÚKOLY",
    blockers: "BLOKACE",
    proposals: "NÁVRHY",
    goals: "GOALS",
    workers: "WORKERS",
    economy: "EKONOMIKA",
    brain: "BRAIN",
    departments: "ODDĚLENÍ",
    audit: "AUDIT",
    rail: "HLAVNÍ NAVIGACE",
    palette: "PŘÍKAZOVÁ PALETA",
    account: "ÚČET",
    settings: "NASTAVENÍ",
  },
  topbar: {
    theme: "MOTIV",
    status: "STAV",
    statusOperational: "● OPERAČNÍ",
    statusDegraded: "● DEGRADOVANÝ",
    time: "ČAS",
  },
  auth: {
    signIn: "PŘIHLÁSIT SE",
    email: "E-MAIL",
    password: "HESLO",
    signingIn: "PŘIHLAŠOVÁNÍ…",
    logout: "ODHLÁSIT SE",
    accessSubtitle: "PŘÍSTUP DO ŘÍDICÍHO CENTRA",
  },
  /**
   * Popisky formulářových polí. Byly psané přímo v komponentách, takže se
   * tentýž údaj jmenoval jednou „NÁZEV", jinde „NÁZEV APLIKACE" — a překlad
   * by je musel hledat po souborech.
   */
  fields: {
    name: "NÁZEV",
    description: "POPIS",
    note: "POZNÁMKA",
    type: "TYP",
    kind: "DRUH",
    role: "ROLE",
    scope: "SCOPE",
    project: "PROJEKT",
    department: "ODDĚLENÍ",
    priority: "PRIORITA",
    deadline: "DEADLINE",
    color: "BARVA",
    label: "LABEL",
    version: "VERZE",
    metric: "METRIKA",
    metricOptional: "METRIKA (volitelně)",
    target: "CÍL",
    targetNumber: "CÍL (číslo)",
    projectName: "NÁZEV PROJEKTU",
    appName: "NÁZEV APLIKACE",
    milestoneName: "NÁZEV MILESTONU",
    nameOptional: "NÁZEV (volitelně)",
    codebaseName: "NÁZEV (např. api, web-fe)",
    repoUrl: "REPOSITORY URL",
    repoUrlOptional: "REPOSITORY URL (volitelně)",
    purpose: "POPIS / ÚČEL",
    stack: "STACK (čárkami)",
    defaultBranch: "DEFAULT BRANCH",
    devBranch: "DEV BRANCH (volitelně)",
    linkToCompanyGoal: "NAPOJIT NA COMPANY GOAL",
    acceptanceCriteria: "ACCEPTANCE CRITERIA (řádek = kritérium)",
    newFolder: "NEBO NOVÁ SLOŽKA",
    nodeNote: "POZNÁMKA (pod uzlem)",
  },

  common: {
    open: "OTEVŘÍT",
    all: "VŠE",
    save: "ULOŽIT",
    cancel: "ZRUŠIT",
    create: "VYTVOŘIT",
    close: "ZAVŘÍT",
    back: "ZPĚT",
    loading: "NAČÍTÁNÍ…",
    none: "—",
    error: "Něco se pokazilo",
    today: "dnes",
    forbidden: "K této části nemáš přístup.",
  },
  commandCenter: {
    heroRunning: "Factory běží.",
    heroNoDecisions: "Žádná rozhodnutí nečekají.",
    heroDecisions: (n: number) =>
      n === 1 ? "Potřebuje od tebe 1 rozhodnutí." : `Potřebuje od tebe ${n} rozhodnutí.`,
    orbitTitle: "PORTFOLIO // ORBITA = ATTENTION SCORE",
    orbitTable: "TABULKA →",
    factoryCore: "FACTORY CORE",
    activeSessions: "AKTIVNÍ SESSIONS",
    tasksToday: (n: number) => `▲ ${n} TASKŮ DNES`,
    talkToJarvis: "MLUVIT S JARVISEM",
    decisionInbox: "DECISION INBOX",
    liveWorkers: "LIVE WORKERS",
    activityStream: "ACTIVITY STREAM",
    milestones: "MILESTONES // NEJBLIŽŠÍ",
    goals: "GOALS",
    attention: "ATTENTION // HOTSPOT",
    doneToday: "DNES DOKONČENO",
    tokens24: "TOKENS / 24H",
    projectsOnline: "projektů",
    decide: "ROZHODNOUT",
    title: "Command Center",
    eventsToday: "událostí dnes",
    eventsTodayLabel: "UDÁLOSTI DNES",
    audited: "vše auditováno",
    workersOnline: (n: number) => `${n} online`,
    urgent: (n: number) => `${n} urgentních`,
    nothingWaits: "nic nečeká",
    noneUrgent: "žádné urgentní",
    incidents: "INCIDENTY",
    noIncidents: "klid",
    projects: "PROJEKTY",
    pausedCount: (n: number) => `${n} pozastaveno`,
    allRunning: "všechny běží",
    factoryReady: "FACTORY-READY",
    factoryReadyNote: "vývoj dokončen",
    automation: "AUTOMATIKA",
    automationScope: "// VŠECHNY PROJEKTY",
    automationNote: "Co který projekt drží a kdy bude hotový. Kliknutí vede na detail.",
    viewTable: "Tabulka",
    viewOrbit: "Orbita",
    colProject: "PROJEKT",
    colRounds: "KOLA 1–6",
    roundState: {
      done: "splněno",
      running: "běží",
      waiting: "čeká na spouštěč",
      blocked: "drží brána",
    } as Record<string, string>,
    colGate: "STAV / BRÁNA",
    colAttention: "ATTENTION",
    colReady: "FACTORY-READY",
    legendReady: "vývoj dokončen",
    legendBlocked: "drží rozhodnutí",
    legendWaiting: "čeká na spouštěč",
    legendRunning: "běží task",
    holdPaused: "lifecycle PAUSED — kola nevznikají, claim stojí",
    holdArchived: "archivováno",
    holdDecisions: (n: number) =>
      n === 1 ? "čeká na 1 rozhodnutí" : `čeká na ${n} rozhodnutí`,
    holdReady: "vývoj dokončen — čeká na tebe",
    holdIdle: "nic neběží",
    noWorker: "Žádný worker připojen.",
    noWorkerNote: "Připoj Claude Desktop přes Workers → token.",
    workersIdle: "Workery čekají na claim.",
    working: "pracuje",
    noGoals: "Zatím žádné goals.",
    noPlan: "projekt bez plánu",
    days: "dní",
    solveIncident: "Řešit incident",
    attentionHotspot: "ATTENTION",
    decide2: "Rozhodnout",
    live: "LIVE",
  },
  projects: {
    title: "Projekty",
    subtitle: "Řazeno podle Attention Score — nahoře je to, co nejvíc čeká na tebe.",
    newProject: "+ NOVÝ PROJEKT",
    att: "ATT",
    project: "PROJEKT",
    status: "STATUS",
    milestone: "MILESTONE",
    running: "PRÁVĚ BĚŽÍ",
    paused: "paused",
    emptyTitle: "Zatím žádné projekty",
    emptyBody: "Založ první projekt — onboarding s AI analýzou přijde ve Fázi 2.",
  },
  project: {
    automation: "AUTOMATIKA",
    automationRounds: "// 6 SYSTÉMOVÝCH KOL",
    standsOnGate: "STOJÍ NA BRÁNĚ",
    cascadeRuns: "KASKÁDA BĚŽÍ",
    whatHolds: "CO PROJEKT DRŽÍ",
    ofGates: "ze",
    whenReady: "Kdy to bude hotové",
    readyConditions: "Tři podmínky současně — dokud neplatí všechny, projekt není hotový.",
    planTicked: "Plán vývoje odškrtnutý",
    planRemaining: (n: number) => (n === 0 ? "hotovo" : `zbývá ${n} položek`),
    planMissing: "plán vývoje zatím není",
    noOpenWork: "Žádná otevřená práce",
    tasksUnit: "tasků",
    inQa: (n: number) => `${n} čeká v QA gate`,
    qaEmpty: "QA fronta prázdná",
    cleanTestRound: "Poslední testovací kolo čisté",
    findings: "nálezů",
    testRoundNote: "nálezy z posledního kola testera — každý je task",
    testRoundClean: "poslední kolo testera nenašlo nic",
    noTestRound: "testovací kolo zatím neproběhlo",
    nowRunning: "PRÁVĚ BĚŽÍ",
    unclaimed: "nikdo neclaimoval",
    nothingRuns: "Nic neběží.",
    nothingRunsNote: "Worker si vezme task, jakmile bude claimovatelný.",
    waitsForYou: "ČEKÁ NA TEBE",
    nothingWaits: "Nic nečeká — Factory jede sama.",
    blockedTasks: "BLOCKED TASKY",
    toBoard: "na desku",
    nothingBlocked: "Nic není blokované.",
    lastEvents: "POSLEDNÍ UDÁLOSTI",
    noMilestone: "žádný otevřený",
    notReadyYet: "vývoj běží",
    devDone: "vývoj dokončen",
    blockedNote: "čeká na rozhodnutí nebo task",
    urgentDecisions: (n: number) => `${n} urgentní → inbox`,
    toInbox: "→ inbox",
    healthUnknown: "zatím není z čeho počítat",
    healthClean: "bez nálezů",
    breakdown: "rozpad",
    healthBreakdown: "HEALTH // ROZPAD",
    back: "← PROJEKTY",
    pause: "POZASTAVIT PROJEKT",
    resume: "OBNOVIT PROJEKT",
    factoryReady: "🚀 READY TO PROD",
    health: "HEALTH",
    attention: "ATTENTION",
    lifecycle: "LIFECYCLE",
    milestone: "MILESTONE",
    factoryReadyLabel: "FACTORY-READY",
    activeTasks: "AKTIVNÍ TASKY",
    blocked: "BLOCKED",
    decisions: "ROZHODNUTÍ",
    priority: "PRIORITA",
    members: "PŘÍSTUP",
    description: "POPIS",
    recentEvents: "POSLEDNÍ UDÁLOSTI",
    tabs: {
      overview: "OVERVIEW",
      settings: "SETTINGS",
    },
    addMember: "PŘIDAT ČLENA",
    lifecycleNote: "Lifecycle mění chování AI — autonomii, proposals a deploy pravidla.",
    statusLine: "STAVOVÝ ŘÁDEK",
    reasonLabel: "DŮVOD ZMĚNY (volitelný, do auditu)",
  },
  devPlan: {
    header: "PLÁN VÝVOJE // KNOWLEDGE → TASKY",
    note: "checklist derivovaný z Knowledge — udržuje AI",
    updated: "aktualizováno",
    progress: (done: number, total: number) => `${done}/${total} hotovo`,
    emptyTitle: "Plán vývoje zatím neexistuje",
    emptyBody: "vznikne při příštím zpracování podkladů nebo doplňovacím kole „Doplnit plán práce“",
    noChecklist: "Dokument zatím neobsahuje checklist — zobrazen surový obsah:",
    untitledSection: "Bez sekce",
  },
  knowledge: {
    uploads: "UPLOADS",
    uploadsNote: "raw podklady — nikdy se nepřepisují",
    upload: "+ NAHRÁT",
    dropzone: "přetáhni MD / PDF / ZIP",
    dropzoneNote: "AI worker je zpracuje do Knowledge",
    structured: "STRUCTURED KNOWLEDGE",
    structuredNote: "source of truth — udržuje AI",
    reprocess: "↻ ZPRACOVAT ZNOVU",
    reprocessConfirm: (filename: string) =>
      `Znovu zpracovat „${filename}" do Knowledge? Worker do minuty dostane prioritní task a existující dokumenty aktualizuje.`,
    newDoc: "+ DOKUMENT",
    exportMd: "⭳ EXPORT MD",
    currentTruth: "AKTUÁLNÍ PRAVDA",
    superseded: "SUPERSEDED",
    archived: "ARCHIV",
    history: (n: number) => `HISTORIE (${n})`,
    edit: "UPRAVIT",
    changeSummary: "PROČ SE PRAVDA MĚNÍ (do provenance)",
    provenance: "PROVENANCE // ODKUD SE TATO PRAVDA VZALA",
    conflictTitle: "⚠ KNOWLEDGE CONFLICT // VYŽADUJE TVÉ ROZHODNUTÍ",
    conflictA: "VARIANTA A · SOUČASNÁ PRAVDA",
    conflictB: "VARIANTA B · NOVÝ ZDROJ",
    conflictRecommended: "(AI DOPORUČUJE)",
    pickA: "PLATÍ A",
    pickB: "✓ PLATÍ B",
    discussJarvis: "PROBRAT S JARVISEM",
    resolvedNote: (choice: string) =>
      `Konflikt vyřešen: platí ${choice}. Source of truth aktualizován, provenance doplněna, audit zapsán.`,
    newContentLabel: "NOVÁ PODOBA DOKUMENTU (volitelně uprav před potvrzením)",
    confirmResolve: "POTVRDIT ROZHODNUTÍ",
    emptyDoc: "Vyber dokument vlevo, nebo založ nový.",
    emptyTree: "Zatím žádná strukturovaná knowledge.",
    noUploads: "Zatím žádné podklady.",
    source: "ZDROJ",
    created: "VYTVOŘENO",
    lastChange: "POSLEDNÍ ZMĚNA",
    why: "PROČ",
    revisions: "REVIZE",
    folder: "SLOŽKA",
    title_: "NÁZEV",
    content: "OBSAH (markdown)",
    readWhen: "KDY SI HO WORKER MÁ NAČÍST (spouštěč)",
    readWhenPlaceholder: "např. hledáš, jak se počítá záloha",
    readWhenNote:
      "Bez spouštěče se dokument workerovi vůbec nenabídne — v jeho rozcestníku se neobjeví a nikdy si ho nepřečte.",
    noTrigger: "BEZ SPOUŠTĚČE",
    noTriggerDetail:
      "Dokument nemá `kdy` — v rozcestníku workera se neobjeví a worker si ho nikdy nenačte. Doplň spouštěč v UPRAVIT.",
    readWhenLabel: "SPOUŠTĚČ",
  },
  apps: {
    header: "APPLICATIONS // DESIGN SE VÁŽE KE KONKRÉTNÍ APLIKACI",
    newApp: "+ NOVÁ APLIKACE",
    codebase: "codebase:",
    noCodebase: "bez codebase",
    designVersions: "DESIGN VERZE",
    noDesign: "bez design inputu — interní utilita",
    uploadZip: "+ nahrát ZIP export (Claude Design)",
    current: "AKTUÁLNÍ",
    setCurrent: "NASTAVIT",
    empty: "Zatím žádné aplikace.",
    emptyNote: "Aplikace = uživatelské celky projektu (Admin, web, mobilní app…).",
  },
  codebases: {
    newCodebase: "+ NOVÁ CODEBASE",
    branches: "BRANCHES",
    policy: "POLICY",
    stack: "STACK",
    map: "CODEBASE MAP",
    mapBy: "vztahy udržuje Claude / onboarding",
    mapNote: "šipka = závislost (kdo volá koho)",
    mapEmpty: "Jeden repozitář — mapa není potřeba.",
    mapNoDeps:
      "Mezi repozitáři zatím není zaznamenaná žádná závislost — mapa je proto jen seznam. Kdo koho volá zapíše worker při onboardingu, nebo to přidáš níž ručně.",
    addDep: "PŘIDAT ZÁVISLOST",
    inRepo: (repo: string) => `(v ${repo} repu)`,
    empty: "Zatím žádné codebases.",
    emptyNote: "Codebase = git repozitář. Projekt jich může mít víc (spec: Project ≠ repo).",
    dod: "DEFINITION OF DONE",
    dodHint: "Řádek = check: key | popis | příkaz (volitelný). Workery párují test reporty přes key.",
    dodDefault: "Vlastní DoD není — platí výchozí sada (unit, lint, build, acceptance).",
  },
  projectSettings: {
    lifecycle: "PROJECT LIFECYCLE // MĚNÍ CHOVÁNÍ AI",
    businessPriority: "BUSINESS PRIORITA",
    access: "PŘÍSTUP",
    policies: "AUTONOMY POLICIES",
    policiesNote: "per akce · Constitution nelze obejít",
    departments: "ODDĚLENÍ",
    departmentsNote: "bez vybraného oddělení nejde založit task s oddělením",
    hostingServer: "HOSTING SERVER",
    hostingServerNote:
      "Coolify server, na kterém projekt běží. Podle něj Factory čte buildy a workerům vydá token. Vybrat jde jen server, který vlastníš.",
    hostingServerDefault: (name: string | null) => (name ? `výchozí (${name})` : "výchozí — žádný není nastavený"),
    hostingServerChanged: "Uloženo. Mapping prostředí z původního serveru je potřeba nahlásit znovu.",
    hostingServerSaved: "✓ Uloženo",
    environments: "ENVIRONMENTS",
    addEnv: "+ ENVIRONMENT",
    dod: "DEFINITION OF DONE",
    dodNote: "konfigurovatelné per codebase ve Fázi 5",
    constitution: "FACTORY CONSTITUTION // NADŘAZENO",
    constitutionText:
      "secrets nikdy do knowledge/kontextu · critical security finding blokuje deploy · destruktivní produkční akce = explicitní oprávnění · významná rozhodnutí auditovatelná",
    dangerZone: "DANGER ZONE",
    deleteProject: "SMAZAT PROJEKT",
    deleteProjectNote:
      "Nevratně smaže projekt včetně aplikací, tasků, knowledge, uploadů i sessions. Audit log zůstane.",
    deleteConfirmText: (slug: string) => `Pro potvrzení napiš slug projektu: ${slug}`,
    deleteConfirmPlaceholder: "slug projektu",
    deleteConfirmAction: "NEVRATNĚ SMAZAT",
  },
  wizard: {
    templateNote: "Šablona určuje i druh projektu. Vybraný druh:",
    noTemplate: "Jiný projekt",
    noTemplateNote: "Bez šablony — prostředí i knowledge složky si nastavíš sám v dalších krocích.",
    title: "Nový projekt",
    subtitle: "kroky mimo základ jsou volitelné",
    step: (n: number, total: number) => `KROK ${n} / ${total}`,
    optional: "· volitelné",
    skipped: "· přeskočeno",
    back: "← ZPĚT",
    next: "POKRAČOVAT →",
    skip: "PŘESKOČIT",
    cancel: "ZRUŠIT",
    create: "VYTVOŘIT PROJEKT →",
    creating: "VYTVÁŘÍM…",
    steps: {
      basics: "Základy projektu",
      template: "Typ / šablona",
      applications: "Aplikace",
      departments: "Oddělení",
      uploads: "Podklady (Knowledge)",
      design: "Design aplikací",
      codebases: "Codebases",
      environments: "Environments & deploy",
      connections: "Connections",
      policies: "Autonomie",
      goals: "Goals",
      milestone: "Milestone / deadline",
      summary: "Shrnutí & vytvoření",
    },
    departmentsHint:
      "Která oddělení jsou pro projekt relevantní? Výběr jde kdykoli změnit v nastavení projektu.",
    goalsHint: "Úvodní cíle projektu — metriku a target lze doplnit později v modulu Goals.",
    milestoneHint: "První milestone s deadlinem — tasky k němu přiřadíš na kanbanu.",
    connectionsHint:
      "Které integrace projekt používá? Secrets (API klíče) doplníš po vytvoření v modulu Connections.",
    connectionsCustom: "VLASTNÍ INTEGRACE (název)",
    noMilestone: "bez milestonu",
    summaryNote:
      "Po vytvoření projektu vznikne automaticky kickoff task — worker nastuduje podklady (Knowledge, Design, codebases) a navrhne plán tasků.",
  },
  pitch: {
    title: "PITCH PERFECT // KONTROLA KVALITY UI",
    subtitle: "worker průběžně nahrává screenshoty — schval, nebo napiš připomínky",
    empty: "Zatím žádné screenshoty.",
    emptyNote:
      "Worker nahrává screenshoty všech obrazovek, modalů a formulářů průběžně při práci na UI. Objeví se tady k revizi.",
    approve: "✓ SCHVÁLIT",
    requestChanges: "✕ PŘIPOMÍNKY",
    reopen: "↺ ZNOVU K REVIZI",
    notePlaceholder: "Co se ti nelíbí? Worker z připomínek udělá tasky…",
    viewportNotePlaceholder: (label: string) =>
      `Připomínka k variantě ${label}… (prázdné = bez připomínky)`,
    sendNote: "ODESLAT PŘIPOMÍNKY",
    statusPending: "ČEKÁ NA REVIZI",
    statusApproved: "SCHVÁLENO",
    statusChanges: "PŘIPOMÍNKY",
    awaitingWorker: "worker zpracuje připomínky do tasků",
    processedInto: "připomínky převedeny na:",
    version: (v: number) => `v${v}`,
    filterAll: "VŠE",
    summary: (a: number, t: number) => `${a}/${t} schváleno`,
    viewportLabel: { DESKTOP: "DESKTOP", MOBILE: "MOBIL", TABLET: "TABLET" } as Record<
      string,
      string
    >,
    viewportMissing: "zatím chybí",
    preview: "▸ PREVIEW",
    openFull: "OTEVŘÍT V PLNÉ VELIKOSTI ↗",
    groupOther: "BEZ APLIKACE",
    colScreen: "OBRAZOVKA",
    colViewports: "VIEWPORTY",
    colStatus: "STAV",
    noShot: "bez screenshotu",
    generalNote: "OBECNÁ PŘIPOMÍNKA",
    savedNote: "poslední připomínka:",
    close: "ZAVŘÍT",
    back: "ZPĚT",
    fromTask: "z tasku:",
    deleteConfirm: (title: string) => `Smazat obrazovku ${title} včetně všech screenshotů?`,
  },
  feedback: {
    title: "FEEDBACK // TVÉ PŘIPOMÍNKY",
    subtitle: "jednosměrný kanál — worker z připomínek udělá tasky",
    empty: "Zatím žádné připomínky.",
    emptyNote: "Napiš, co chceš změnit nebo co se ti nelíbí; můžeš přiložit screenshot.",
    placeholder: "Napiš připomínku… (Enter = odeslat, Shift+Enter = nový řádek)",
    attach: "+ OBRÁZEK",
    send: "ODESLAT",
    waiting: "čeká na zpracování workerem",
    processedInto: "převedeno na:",
    processedEmpty: "zpracováno bez tasků",
  },
  kanban: {
    department: "ODDĚLENÍ:",
    all: "VŠE",
    workflowNote: (wf: string) =>
      `workflow: ${wf} · Effective Priority = priorita + deadline + blokované downstream`,
    newTask: "+ TASK",
    foreignState: (wf: string) =>
      `Skutečný stav ve workflow ${wf.toUpperCase()} — přesun je možný jen ve filtru oddělení.`,
    blockedBy: (n: number) => `⛔ čeká na ${n} ${n === 1 ? "task" : "tasky"}`,
    waitingDecision: "⛔ čeká na rozhodnutí",
    empty: "—",
  },
  task: {
    attachmentDrop: "+ SCREENSHOT — PŘETÁHNI, VLOŽ (⌘V) NEBO VYBER",
    attachmentUploading: "NAHRÁVÁM…",
    attachmentsFull: "Task má maximum 10 příloh.",
    backToKanban: (p: string) => `← ${p} / KANBAN`,
    doNext: "⚡ DO NEXT",
    undoNext: "⚡ ZRUŠIT OVERRIDE",
    pause: "⏸ PAUSE",
    stop: "✕ STOP",
    description: "POPIS",
    acceptance: "ACCEPTANCE CRITERIA",
    planTitle: "PLÁN & CHECKPOINTY",
    planNote: "zapisuje agent po claimu (Fáze 4)",
    planEmpty: "Zatím žádný plán — vznikne po claimu workerem.",
    discussion: "AI DISCUSSION // AUDITOVATELNÁ KOMUNIKACE",
    comment: "PŘIDAT KOMENTÁŘ",
    parent: "PARENT FEATURE",
    subtasks: (n: number, c: number) => `${n} subtasků${c ? ` · ${c} codebases` : ""}`,
    parentQaNote: "QA ověří nejen subtasky, ale feature jako celek (spec §28).",
    ep: "EFFECTIVE PRIORITY",
    dependencies: "DEPENDENCIES",
    noDependencyMatch: "Nic neodpovídá — zkus jiný název nebo číslo.",
    addDependency: "PŘIDAT ZÁVISLOST",
    noDeps: "žádné",
    artifacts: "ARTIFACTS & SESSIONS",
    artifactsNote: "Branch, commity, PR a sessions přijdou ve Fázi 4.",
    qa: "QA // DEFINITION OF DONE",
    qaPassed: "✓ QA PASSED",
    qaChanges: "✗ CHANGES REQUESTED",
    qaNotePlaceholder: "Poznámka QA (nálezy, co ověřit)…",
    qaAwaiting: "Task čeká na QA verdikt — ověř DoD a akceptační kritéria.",
    qaNoRuns: "Zatím žádné reportované testy.",
    qaRework: (n: number) => `REWORK ×${n}`,
    qaBy: (label: string, when: string) => `${label} · ${when}`,
    testRuns: "TEST RUNS",
    gitArtifacts: "GIT ARTIFACTS",
    gitArtifactsEmpty: "Zatím žádné — branch, commity a PR reportují worker sessions.",
    pitchScreens: "PITCH PERFECT",
    openPitch: "OTEVŘÍT PITCH PERFECT ↗",
    traceability: "TRACEABILITY",
    status: "STAV",
    moveTo: "PŘESUNOUT DO",
    notFound: "Task neexistuje.",
  },
  decisions: {
    title: "Rozhodnutí",
    lead: (n: number) =>
      n === 0
        ? "Jediné místo, kde tě systém potřebuje. Teď nic nečeká."
        : `Jediné místo, kde tě systém potřebuje. ${n} ${n === 1 ? "položka čeká" : n < 5 ? "položky čekají" : "položek čeká"}.`,
    waiting: (n: number) => `${n} čeká`,
    emptyTitle: "Inbox je prázdný",
    emptyNote: "Factory pracuje autonomně. Ozve se, až tě bude potřebovat.",
    emptyDetail: "Vše rozhodnuto.",
    emptyDetailNote: "Factory pokračuje.",
    context: "KONTEXT",
    recommendation: "AI DOPORUČENÍ",
    alternatives: "ALTERNATIVY",
    choose: "ZVOLIT →",
    blocks: "BLOKUJE",
    blocksProjectTag: "STOJÍ PROJEKT",
    blocksProjectNote:
      "Tahle překážka zastavila víc tasků, takže se z projektu nic neclaimuje, dokud ji nerozhodneš.",
    impactTitle: "DOPAD",
    approve: "✓ SCHVÁLIT DOPORUČENÍ",
    reject: "✕ ZAMÍTNOUT",
    discuss: "◉ PROBRAT S JARVISEM",
    discussPrefill: (title: string) => `Proberme rozhodnutí „${title}“ — co doporučuješ a proč?`,
    customLabel: "VLASTNÍ KOMENTÁŘ / NÁVRH",
    customPlaceholder: "Nelíbí se ti žádná varianta? Napiš vlastní návrh nebo komentář…",
    customHint: "Text se uloží jako poznámka ke zvolenému rozhodnutí. Tlačítkem vpravo rozhodneš čistě podle svého návrhu.",
    customSubmit: "✎ ROZHODNOUT PO SVÉM",
    confirmTitle: "POTVRDIT ROZHODNUTÍ",
    confirmApprove: "Schválit AI doporučení",
    confirmReject: "Zamítnout rozhodnutí",
    confirmAlternative: (label: string) => `Zvolit alternativu: ${label}`,
    confirmCustom: "Rozhodnout vlastním řešením",
    confirmNoteLabel: "POZNÁMKA",
    confirmSubmit: "✓ POTVRDIT",
    afterNote: "Po rozhodnutí: blokované tasky se odblokují · zapíše se audit záznam",
    conflictGoto: "OTEVŘÍT V KNOWLEDGE →",
    conflictTag: "KNOWLEDGE CONFLICT",
    resolvedToast: (n: number) => (n > 0 ? `Rozhodnuto · odblokováno ${n} tasků` : "Rozhodnuto"),
    undo: "VRÁTIT ZPĚT",
    undoneToast: "Rozhodnutí vráceno do inboxu",
    blockerTag: "Z BLOKACE",
    reclassify: "TOHLE NENÍ ROZHODNUTÍ",
    reclassifyHint:
      "Rozhodnutí má aspoň dvě varianty a tvoje volba mění, co se postaví. Nemá-li je, patří položka jinam — obsah se přenese a nic se nemaže.",
    reclassifyToHumanTask: "→ ÚKOL PRO ČLOVĚKA",
    reclassifyToProposal: "→ NÁVRH",
    reclassifyHumanTaskNote:
      "Výsledek je předem jasný, jen to musí udělat člověk. Blokované tasky čekají dál — teď na nález.",
    reclassifyProposalNote:
      "Nápad AI mimo scope. Tasky, které na rozhodnutí čekaly, se pustí dál.",
    reclassifiedToast: (target: string) => `Přeřazeno na ${target}`,
    looksLikeHumanTask: "Bez doporučení, bez alternativ a nic to neblokuje — nevypadá to na rozhodnutí.",
  },

  /**
   * Úkoly pro člověka. Vlastní obrazovka proto, že úkol ownera zdrží o minuty,
   * kdežto blokace stojí celý cyklus workera — nejsou to stejně naléhavé věci
   * a v jedné schránce to nešlo poznat.
   */
  humanTasks: {
    title: "Úkoly pro člověka",
    lead: (n: number) =>
      n === 0
        ? "Věci, které nemůže udělat worker. Teď nic nečeká."
        : `Věci, které nemůže udělat worker. ${n} ${n === 1 ? "úkol čeká" : n < 5 ? "úkoly čekají" : "úkolů čeká"}.`,
    emptyTitle: "Žádné úkoly",
    emptyNote: "Nic nečeká na tvoje ruce ani oči.",
    emptyDetail: "Hotovo.",
    emptyDetailNote: "Factory pokračuje.",
    whatToDo: "CO UDĚLAT",
    whyHuman: "PROČ TO NEMŮŽE WORKER",
    expectedFinding: "CO ZJISTIT",
    blocks: "BLOKUJE",
    blocksProjectTag: "STOJÍ PROJEKT",
    blocksProjectNote:
      "Tenhle úkol zastavil víc tasků, takže se z projektu nic neclaimuje, dokud ho neuzavřeš.",
    findingLabel: "TVŮJ NÁLEZ",
    findingPlaceholder: "Co jsi zjistil? Např. „diakritika OK, zalomení sedí“ nebo „na třetí pásce chybí háčky“…",
    findingHint:
      "Tenhle text dostane worker jako VSTUP do práce, ne jako příznak hotovo — piš ho tak, aby podle něj šlo pokračovat.",
    submit: "✓ ZAPSAT NÁLEZ",
    dismiss: "✕ NEDĚLÁ SE",
    dismissLabel: "PROČ SE TO DĚLAT NEBUDE",
    discuss: "◉ PROBRAT S JARVISEM",
    discussPrefill: (title: string) => `Proberme úkol „${title}“ — co přesně mám zjistit?`,
    confirmTitle: "POTVRDIT NÁLEZ",
    confirmDismissTitle: "POTVRDIT ZRUŠENÍ ÚKOLU",
    confirmSubmit: "✓ POTVRDIT",
    afterNote: "Po zapsání: nález jde do diskuse tasku · blokované tasky se odblokují · zapíše se audit",
    doneToast: (n: number) => (n > 0 ? `Nález zapsán · odblokováno ${n} tasků` : "Nález zapsán"),
    dismissedToast: "Úkol uzavřen jako „nedělá se“",
    undo: "VRÁTIT ZPĚT",
    undoneToast: "Úkol vrácen mezi otevřené",
    findingRequired: "Bez nálezu se úkol uzavřít nedá — jeho obsah je celý smysl úkolu.",
    closedFinding: "NÁLEZ",
  },

  /**
   * Blokace. Data existovala od začátku, obrazovka ne — proto se jich nasbíralo
   * kolem dvaceti, aniž o nich kdokoli věděl.
   */
  blockers: {
    title: "Blokace",
    lead: (n: number) =>
      n === 0
        ? "Co worker neudělal nebo obešel kvůli překážce. Teď nic neleží."
        : `Co worker neudělal nebo obešel kvůli překážce. ${n} ${n === 1 ? "otevřená" : n < 5 ? "otevřené" : "otevřených"}.`,
    emptyTitle: "Žádné otevřené blokace",
    emptyNote: "Workeři na nic nenaráží.",
    emptyDetail: "Nic neblokuje.",
    emptyDetailNote: "Factory pokračuje.",
    showResolved: "ZOBRAZIT ODBAVENÉ",
    hideResolved: "JEN OTEVŘENÉ",
    reportedBy: "NAHLÁSIL",
    context: "KDE VZNIKLA",
    occurrences: (n: number) => `${n}× NAHLÁŠENO`,
    occurrencesNote:
      "Táž překážka hlášená opakovaně je silnější signál než jednorázová — odbavením se uzavřou všechny výskyty.",
    firstReported: "poprvé",
    openSession: "OTEVŘÍT SESSION →",
    openTask: "OTEVŘÍT TASK →",
    linkedDecision: "Z blokace vzniklo rozhodnutí — jeho vyřešení blokaci odbaví samo.",
    resolveLabel: "ČÍM PŘEKÁŽKA POMINULA",
    resolvePlaceholder: "Konkrétní příčina a konkrétní postup, kterým to pominulo. „Vyřešeno“ nikomu nepomůže…",
    resolveHint:
      "Tvoje poznámka je jediné, co si z překážky odnese další worker — dostane ji, až narazí na totéž.",
    lessonLabel: "POUČENÍ DO BRAINU (volitelné)",
    lessonTitlePlaceholder: "Název poučení…",
    lessonReadWhenPlaceholder: "Kdy si ho vybavit — např. „delegace selže na command not found“…",
    lessonBodyPlaceholder: "Co si z toho odnést do příště (markdown, odkazy [[slug]])…",
    lessonHint:
      "Přilož jen to, co je obecné a příště změní postup. Z brainu plného zápisků nikdo nic nenajde.",
    lessonFactoryScope: "PLATÍ NA VŠECH PROJEKTECH",
    submit: "✓ ODBAVIT BLOKACI",
    confirmTitle: "POTVRDIT ODBAVENÍ",
    confirmSubmit: "✓ ODBAVIT",
    resolvedToast: (n: number) => (n > 1 ? `Odbaveno · ${n} výskytů téže překážky` : "Blokace odbavena"),
    resolvedTag: "ODBAVENO",
    afterNote: "Po odbavení: poznámka jde do diskuse tasku · založí se poučení do brainu · zapíše se audit",
    noteRequired: "Napiš, čím překážka pominula a jak jsi to ověřil.",
    truncated: (shown: number, total: number) =>
      `Vypsáno ${shown} z ${total} — zbytek se do seznamu nevešel. Zužte filtrem projektu.`,
    workaroundsTitle: "OBCHVATY // K ANALÝZE, NE K ODBAVENÍ",
    workaroundsNote: (total: number, groups: number) =>
      `Co workeři obešli, ale task přesto dokončili: ${total} hlášení, ${groups} různých. Opakovaný obchvat je kandidát na úpravu Factory nebo workera.`,
  },

  /** Návrhy AI — vlastní obrazovka nad modelem Proposal. */
  proposals: {
    title: "Návrhy AI",
    lead: (n: number) =>
      n === 0
        ? "Nápady AI mimo schválený scope. Teď žádný nečeká."
        : `Nápady AI mimo schválený scope. ${n} ${n === 1 ? "čeká" : "čeká"}.`,
    emptyTitle: "Žádné návrhy",
    emptyNote: "AI zrovna nic nenavrhuje.",
    emptyDetail: "Nic k posouzení.",
    emptyDetailNote: "Factory pokračuje.",
    what: "CO NAVRHUJE",
    why: "PROČ",
    proposedBy: "PODAL",
    approve: "✓ SCHVÁLIT — VZNIKNE TASK",
    reject: "✕ ZAMÍTNOUT",
    discuss: "◉ PROBRAT S JARVISEM",
    discussPrefill: (title: string) => `Proberme návrh „${title}“ — má smysl ho vzít do scope?`,
    noteLabel: "POZNÁMKA (volitelná)",
    notePlaceholder: "Co k tomu chceš dodat — půjde to do zadání tasku…",
    legacyDecision:
      "Tenhle návrh si kvůli zobrazení vyrobil rozhodnutí. Vyřízením tady se zavře i ono.",
    confirmTitle: "POTVRDIT",
    confirmApprove: "Schválit návrh — vznikne task",
    confirmReject: "Zamítnout návrh",
    confirmSubmit: "✓ POTVRDIT",
    approvedToast: (n: number | null) => (n ? `Schváleno · vznikl task #${n}` : "Schváleno"),
    rejectedToast: "Návrh zamítnut",
    undoneToast: "Návrh vrácen mezi čekající",
    afterNote: "Po schválení: vznikne task v projektu · zapíše se audit záznam",
    decidedTag: "VYŘÍZENO",
    showDecided: "ZOBRAZIT VYŘÍZENÉ",
    hideDecided: "JEN ČEKAJÍCÍ",
  },
  plan: {
    title: "Execution Plan",
    subtitle: "Dynamický — přepočítá se při každé změně EP, deadlinu i priority.",
    computed: (when: string) => `poslední přepočet: ${when}`,
    lane: "FACTORY",
    laneCaps: "worker lanes přijdou ve Fázi 4",
    now: "NOW",
    next: "NEXT · PREDIKCE",
    then: "THEN",
    queued: "QUEUED // GLOBÁLNÍ FRONTA PODLE EFFECTIVE PRIORITY",
    queuedNote: "DO NEXT = explicitní human priority override — task přeskočí frontu, zapíše se do auditu",
    acrossProjects: "napříč projekty",
    ep: "EP",
    taskCol: "TASK",
    project: "PROJEKT",
    why: "FAKTORY PRIORITY",
    override: "⚡ HUMAN OVERRIDE",
    doNext: "⚡ DO NEXT",
    idle: "Nic se neprovádí — workery přijdou ve Fázi 4.",
    blocked: (n: number) => `${n} blokovaných tasků mimo frontu`,
  },
  goals: {
    title: "Goals & Roadmap",
    lead: "Od firemního cíle přes iniciativy až k tasku, který ho posouvá.",
    hierarchy: "HIERARCHIE",
    roadmap: "ROADMAP / TIMELINE",
    traceability: "TRACEABILITY",
    newGoal: "+ NOVÝ GOAL",
    companyGoal: "COMPANY GOAL",
    projectGoal: "PROJECT GOAL",
    linked: (g: number, i: number, t: number) => `${g} propojených project goals · ${i} iniciativ · ${t} tasků`,
    initiative: "INICIATIVA",
    addInitiative: "+ INICIATIVA",
    health: { ON_TRACK: "ON TRACK", AT_RISK: "AT RISK", OFF_TRACK: "OFF TRACK" },
    today: "DNES",
    riskNote: "Milestone risk:",
    traceNote: "PROČ TATO PRÁCE EXISTUJE // CESTA OD GOALU K TASKU",
    traceBidirectional: "OBOUSMĚRNÁ DOHLEDATELNOST",
    traceBidirectionalNote:
      "Stejná cesta vede i zpět: z každého tasku dohledáš přes panel Traceability goal, rozhodnutí i release. Odpovídá na otázku: „přispívá tahle práce k něčemu strategickému?“",
    noGoalTasks: "BEZ VAZBY NA GOAL",
    metric: "METRIKA",
    owner: "OWNER",
    deadline: "DEADLINE",
    projectGoalsTitle: "GOALS PROJEKTU // NAPOJENO NA COMPANY GOAL",
    globalGoals: "GLOBÁLNÍ GOALS →",
    empty: "Zatím žádné goals.",
    emptyCompany: "Firemní cíl zatím není. Od něj se odvíjí, co má Factory dělat.",
    emptyProject: "Žádné projektové cíle. Založ je pod firemním cílem — pak je vidět, čím ho který projekt posouvá.",
    projectGoals: "PROJEKTOVÉ CÍLE",
  },
  departments: {
    title: "Oddělení",
    subtitle: "Globální registr — projekt si z něj vybírá jen to, co potřebuje.",
    newDepartment: "+ NOVÉ ODDĚLENÍ",
    active: "AKTIVNÍ",
    disabled: "VYPNUTO",
    workflow: "WORKFLOW:",
    tools: "CAPABILITIES:",
    model: "MODEL:",
    modelDefault: "— (dle workera)",
    modelTitle: "Preferovaný model pro tasky oddělení. Výchozí = nechá se na workeru.",
    autonomy: "AUTONOMIE:",
    stats: (p: number, t: number) => `${p} projektů · ${t} otevřených tasků`,
    builderTitle: "Workflow Builder",
    backToDepartments: "← ODDĚLENÍ",
    addState: "+ PŘIDAT STAV",
    selectedNode: "VYBRANÝ UZEL",
    legend: {
      state: "stav",
      gate: "approval gate",
      handoff: "handoff do jiného departmentu",
      ret: "návratová transition",
    },
    builderNote: "Změny workflow platí pro nové přesuny. Rozpracované tasky dojedou podle původního workflow.",
    save: "ULOŽIT WORKFLOW",
    projectDepartments: "ODDĚLENÍ PROJEKTU",
  },
  releases: {
    header: "RELEASES // CO OBSAHUJÍ · KDO SCHVÁLIL DEPLOY · VERIFIKACE",
    newRelease: "+ RELEASE",
    empty: "Zatím žádné releases.",
    tasksDone: (d: number, t: number) => `${d}/${t} tasků hotovo`,
    deployments: {
      header: "NASAZENÍ // CO KDE BĚŽÍ · COOLIFY · RAILWAY",
      branch: (b: string) => `větev ${b}`,
      empty: "Environment zatím nemá nahlášenou hosting infrastrukturu — založí ji na Coolify a nahlásí worker (deploy-config).",
      zeaburPending:
        "Environment ještě běží na Zeaburu a čeká na přestěhování — stav buildů se nesleduje, níže je poslední známá historie.",
      provider: { COOLIFY: "COOLIFY", RAILWAY: "RAILWAY", ZEABUR: "ZEABUR" } as Record<string, string>,
      hostingLink: "DEPLOY",
      noBuilds: "Zatím žádné buildy.",
      history: "HISTORIE BUILDŮ",
      buildLog: "BUILD LOG",
      logEmpty: "Build log je prázdný (service bez build fáze, např. databáze).",
      logError: "Build log se nepodařilo načíst.",
      open: "OTEVŘÍT",
    },
  },
  economy: {
    title: "Ekonomika provozu",
    lead: "Měrnou jednotkou je % týdenního limitu. Koruny jsou přepočet tarifu účtu — platí jen při jednom workerovi na účet.",
    toWorkers: "stroje",
    weekSpend: "SPOTŘEBA TÝDNE",
    noPrice: "tarif nevyplněn — bez přepočtu na koruny",
    mostExpensive: "NEJDRAŽŠÍ STROJ",
    noTariff: "bez tarifu",
    noUsage: "Žádný stroj zatím nehlásil limity.",
    projectsCount: "PROJEKTY SE SPOTŘEBOU",
    inWindow: (d: number) => `za posledních ${d} dní`,
    accuracy: "PŘESNOST ČÍSEL",
    accuracyOk: "Každý worker má vlastní účet — procenta i koruny sedí.",
    sharedWarning: (accounts: string) =>
      `Sdílený účet (${accounts}): procenta zahrnují i cizí spotřebu, koruny jsou nadsazené.`,
    projectCost: "CENA PROJEKTŮ",
    taskCost: "NEJDRAŽŠÍ TASKY",
    thisWeek: "// TENTO TÝDEN",
    machines: "STROJE A PŘEDPLATNÉ",
    machinesNote: "// ÚČET · TARIF · LIMIT",
    colProject: "PROJEKT",
    colPct: "% LIMITU",
    colPrice: "≈ KČ",
    colTask: "TASK",
    colSessions: "PRŮBĚH",
    sessions: (n: number) => `${n} sessions`,
    rework: (n: number) => `${n}× rework`,
    noAccount: "účet nevyplněn",
    noReset: "reset limitu neznámý",
    resets: (t: string) => `reset ${t}`,
    reported: (t: string) => `hlášeno ${t}`,
    noData: "Zatím žádná spotřeba.",
    noDataNote: "Runner hlásí % limitu po skončení session.",
    roundingNote:
      "/usage vrací celá procenta — levné sessions reportují 0 %. Součet za projekt je věrohodný, cena jedné levné session je šum.",
  },
  workers: {
    title: "Workers",
    subtitle: "Task patří oddělení, worker si na něj bere jen execution lease.",
    connect: "+ PŘIPOJIT WORKER",
    online: "ONLINE",
    offline: "OFFLINE",
    disabled: "VYPNUT",
    lastSeen: (when: string) => `naposledy ${when}`,
    neverSeen: "zatím se nepřipojil",
    claudeVersion: (v: string) => `claude ${v}`,
    steps: "kroků",
    idle: "idle — čeká na claim",
    limitOk: "limit v pořádku",
    limitWarning: "blíží se limitu",
    limitReached: "limit vyčerpán",
    limitTypes: {
      five_hour: "5h",
      seven_day: "týden",
      seven_day_opus: "týden Opus",
      seven_day_overage_included: "týden vč. overage",
    } as Record<string, string>,
    limitResets: (when: string) => `reset ${when}`,
    limitOverage: "čerpá overage",
    /// Zdroj hlásí VYČERPANÁ procenta, uživatele ale zajímá, co zbývá.
    limitRemaining: "zbývá",
    limitWindow5h: "5h",
    limitWindowWeek: "týden",
    /// Kolik z týdenního limitu spolklo jedno spuštění — čitelnější než tokeny.
    sessionCost: (pct: string) => `${pct} týdenního limitu`,
    sessionCostTiny: "<0,1 % týdenního limitu",
    /// Runner limit hlásí průběžně; po pár hodinách je údaj bezcenný.
    limitStale: (when: string) => `limit: údaj z ${when}`,
    /* --- provozní historie a porovnání strojů (p3c) --- */
    compare: "POROVNAT STROJE",
    compareTitle: "Porovnání strojů",
    compareWindow: (days: number) => `okno ${days} dní`,
    compareEmpty: "Zatím není co porovnávat",
    compareEmptyNote: "Data se objeví po prvních dokončených sessions.",
    compareTimelineNote: "výška sloupce = minuty práce v dané hodině · prázdno = stroj stál",
    compareCeiling: "STROP TARIFU",
    compareCeilingNote: "kolik hodin práce tarif uveze, než dojede týdenní limit",
    compareTable: "ČÍSLA VEDLE SEBE",
    compareTableNote: "spotřeba na tisíc kroků nezávisí na rychlosti stroje — jen podle ní se dají tarify srovnat",
    compareMetric: "UKAZATEL",
    compareWorked: "odpracováno",
    compareIdle: "prostoj",
    compareTurns: "kroků",
    comparePer1000Session: "5h okno na 1000 kroků",
    compareCeilingUtil: "strop obsazenosti",
    comparePrice: "cena za měsíc",
    comparePricePerHour: "cena za worker-hodinu",
    compareCaveat:
      "Procenta limitu hlásí runner sám z /usage a zaokrouhluje je na celá čísla, takže krátká session vykáže nulu. Srovnávat má smysl součty, ne jednotlivé běhy — a jen mezi stroji, které za okno odpracovaly srovnatelně dlouho.",
    activityNoWorker: "Stroj nenalezen",
    activityNoWorkerNote: "Buď byl odpojen, nebo odkaz míří na cizí instalaci.",
    activityNoTariff: "tarif nevyplněn",
    activityStarted: "SPUŠTĚNO",
    activityUtilization: "OBSAZENOST",
    activityUtilizationSub: (worked: string, idle: string) => `${worked} práce · ${idle} prostoj`,
    activitySessions: "SESSIONS",
    activitySessionsSub: (done: number, interrupted: number) =>
      `${done} dokončených · ${interrupted} přerušených`,
    activityTasksDone: "HOTOVÉ TASKY",
    activityTurnsSub: (turns: number) => `${turns.toLocaleString("cs-CZ")} kroků`,
    activityCeiling: "STROP TARIFU",
    activityCeilingSub: (util: number) => `${util} % obsazenosti za týden`,
    activityCeilingUnknown: "týdenní okno se nepodařilo ohraničit",
    activityTimeline: "KDY BĚŽEL A KDY STÁL",
    activityTimelineNote: "po hodinách · výška sloupce = minuty práce",
    activityIdle: "PROSTOJ",
    activityIdleNote: "pauzy delší než 30 minut jsou podpis prázdné fronty",
    activityIdleTotal: "celkem",
    activityIdleGaps: "pauz nad 30 min",
    activityIdleLongest: "nejdelší pauza",
    activityAvgSession: "průměrná session",
    activityCapacity: "KAPACITA",
    activityCapacityNote: "co dovolí tarif v aktuálním týdenním okně",
    activityCapacityMissing:
      "Týdenní okno se nepodařilo ohraničit — buď stroj limity nehlásí, nebo je v historii míň spotřeby, než snímek uvádí (typicky sdílený účet). Kapacita by v takovém případě lhala, proto se nepočítá.",
    activityWeekUsed: "vyčerpáno z týdne",
    activityPerHour: "na hodinu práce",
    activityPer1000: "týdenní limit na 1000 kroků",
    activityWindow: "týdenní okno",
    activityExhaustion: "dojede na strop",
    activityExhaustionNone: "v tomhle okně už ne",
    activityDiagnosisQueue:
      "Prostoj je roztroušený do pauz a stroj je pod stropem tarifu — brzdí ho prázdná fronta, ne limit. Přidat práci, nebo mu povolit další projekty.",
    activityDiagnosisBoth:
      "Obsazenost je na stropu tarifu a k tomu jsou v okně dlouhé pauzy — platí obojí: fronta určuje, KDY stroj stojí, ale kolik toho za týden stihne, rozhoduje tarif. Naplnit frontu práci nepřidá, jen ji rozloží rovnoměrněji.",
    activityDiagnosisLimit:
      "Obsazenost je na stropu tarifu — víc práce z tohohle stroje nedostaneš, dokud se nezvedne předplatné. Naplnit frontu nepomůže.",
    activityDiagnosisUnknown:
      "Na diagnózu je zatím málo dat — strop tarifu se spočítá, až stroj odpracuje delší kus týdenního okna.",
    sessionsToday: "WORK SESSIONS",
    modelAnalytics: "MODEL ANALYTICS",
    modelNote: "hodnotíme modely, ne stroje — Claude Desktop je všude stejný",
    routines: "ROUTINES // OPAKOVANÁ PRÁCE",
    routinesNote: "worker bez executable tasku dostane vhodnou routine · výstupy vznikají jako task / proposal / decision / report",
    newRoutine: "+ ROUTINE",
    routineCol: "ROUTINE",
    routineDept: "DEPT",
    routineScope: "SCOPE",
    routineSchedule: "PLÁN",
    routineLastOut: "POSLEDNÍ VÝSTUP",
    routineNext: "DALŠÍ BĚH",
    routineAllProjects: "všechny aktivní projekty",
    routineNoRuns: "zatím neběžela",
    routinePaused: "pozastavena",
    routineRunning: "● právě běží",
    routinesEmpty: "Zatím žádné routines — opakovaná práce se definuje tady.",
    tokenTitle: "WORKER TOKEN — ZOBRAZÍ SE JEN JEDNOU",
    tokenNote: "Ulož ho do Claude Desktop connectoru. Po zavření už token nejde zobrazit.",
    reissue: "NOVÝ TOKEN",
    name: "NÁZEV",
    departments: "POVOLENÁ ODDĚLENÍ (prázdné = všechna)",
    projects: "POVOLENÉ PROJEKTY (prázdné = všechny)",
    projectsOnly: (names: string) => `jen: ${names}`,
    editProjects: "PROJEKTY",
    editBilling: "PŘEDPLATNÉ",
    billing: "ÚČET A TARIF · z nich se počítá cena spotřeby",
    billingAccount: "ÚČET",
    billingTariff: "TARIF",
    billingPrice: "CENA / MĚS",
    billingCurrency: "MĚNA",
    billingHint: (perPct: string, currency: string) =>
      `1 % týdenního limitu ≈ ${perPct} ${currency} · platí jen při jednom workerovi na účet`,
    billingEmpty: "Bez ceny se spotřeba ukáže jen v % týdenního limitu.",
    capabilities: "CAPABILITIES (čárkami)",
    session: "SESSION",
    worker: "WORKER",
    taskCol: "TASK",
    dept: "DEPT",
    model: "MODEL",
    /// Ne tokeny: limit je to, co reálně dochází, a v procentech je to čitelné.
    weekCost: "Z TÝDNE",
    state: "STAV",
    noSessions: "Zatím žádné sessions.",
    noWorkers: "Žádný worker připojen.",
    connectorDocs: "Návod: docs/WORKER_BOOTSTRAP.md (stabilní prompt) — manuál si worker načítá z GET /api/worker/instructions",
    bootstrapTitle: "PROMPT PRO WORKERA // VLOŽ DO INSTRUKCÍ, UŽ SE NEMĚNÍ",
    bootstrapPrompt: (base: string, token: string) =>
      `Jsi worker App Factory.\nBASE = ${base}\nVšechny requesty posílej s hlavičkou Authorization: Bearer ${token}\n\nPravidla:\n1. První krok každého pracovního cyklu: GET BASE/api/worker/instructions — vrácený markdown je aktuální a závazný manuál, řiď se výhradně jím.\n2. Pokud se manuál nepodaří načíst, nepracuj — ohlas problém člověku a skonči.\n3. Cokoli neuděláš/obejdeš kvůli překážce, nahlas přes API dle manuálu — nikdy to nenechávej jen v chatu.\n4. Po dokončení cyklu opakuj od kroku 1.`,
    blockers: "BLOKACE // CO WORKER NEUDĚLAL NEBO OBEŠEL",
    blockersOpenScreen: "OTEVŘÍT BLOKACE →",
    blockersNote:
      "hlášeno workery přes POST …/blockers — reaguj taskem, decision nebo doplněním podkladů; jakmile překážka pomine, worker blokaci sám odbaví (resolve) a zmizí odsud",
    noBlockers: "✓ Žádné otevřené blokace.",
    noBlockersEver: "✓ Žádné blokace nahlášeny.",
    showResolvedBlockers: "+ vyřešené",
    hideResolvedBlockers: "jen otevřené",
    blockerResolved: (by: string, when: string) => `✓ vyřešeno (${by}, ${when})`,
  },

  brain: {
    title: "FACTORY BRAIN",
    subtitle: "poučení z provozu — co nás už jednou kouslo a jak se to obchází",
    scopes: { ALL: "VŠE", FACTORY: "PLATÍ VŠUDE", PROJECT: "JEN PROJEKT" } as Record<string, string>,
    stats: (total: number, draft: number, recurring: number) =>
      `${total} poznámek · ${draft} bez spouštěče · ${recurring} se vrací`,
    listTitle: "POZNÁMKY",
    allProjects: "všechny projekty",
    back: "← ZPĚT NA SEZNAM",
    occurrences: (n: number) => `${n}× se stalo`,
    needsReview: "K REVIZI",
    noTrigger: "bez spouštěče — workerovi se nenabídne",
    emptyGraph: "Brain je zatím prázdný",
    emptyGraphNote: "Poučení vzniká, když worker odbaví blokaci nebo když vyřešíš rozhodnutí.",
    emptyList: "Žádné poznámky",
    emptyListNote: "Zatím se nic nenaučilo — nebo je vše archivované.",
    legendFactory: "platí všude",
    legendProject: "poučení projektu",
    legendMap: "mapa kódu",
    legendDraft: "bez spouštěče / odkaz nikam",
    legendSize: "velikost = kolikrát se to stalo",
    fieldTitle: "NÁZEV",
    fieldWhen: "KDY SI JI NAČÍST",
    fieldWhenPlaceholder: "delegace selže na command not found",
    fieldWhenHelp:
      "Podle tohohle pole se poučení hledá. Bez něj zůstane ležet a worker ho nikdy nedostane.",
    fieldBody: "OBSAH (markdown, odkazy [[slug]])",
    save: "ULOŽIT",
    promote: "POVÝŠIT NA FACTORY",
    promoteHelp: "Povýšit jen to, co nezávisí na projektu — jinak to bude šum na cizích projektech.",
    archive: "ARCHIVOVAT",
  },

  projectBrain: {
    title: "BRAIN PROJEKTU",
    loading: "…",
    note: "Co o projektu víme: mapa kódu (kde co je) a poučení platná jen tady. Worker si obojí stahuje na disk a čte jen to, co odpovídá jeho situaci — proto je u každé položky spouštěč.",
    summary: (maps: number, lessons: number, unmapped: number) =>
      `${maps} map · ${lessons} poučení${unmapped > 0 ? ` · ${unmapped} codebase bez mapy` : ""}`,
    drafts: (n: number) => `${n} bez spouštěče — workerovi se nenabídnou`,
    mapTitle: "MAPA KÓDU",
    expand: "otevřít",
    collapse: "sbalit",
    lessonTitle: "POUČENÍ JEN PRO TENTO PROJEKT",
    noTrigger: "BEZ SPOUŠTĚČE",
    noTriggerNote: "chybí `kdy` — worker si to nikdy nedotáhne",
    verifiedAt: "ověřeno na",
    noCommit: "bez commitu ověření — nejde poznat, jestli mapa ještě platí",
    chars: (n: number) => `${(n / 1000).toFixed(1).replace(".", ",")} kB`,
    unmapped: "nemá mapu — čeká na kolo „Zmapovat codebase“",
    noCodebases: "Projekt nemá codebase, není co mapovat.",
    noLessons: "Žádné poučení jen pro tenhle projekt",
    noLessonsNote: "Vzniká z odbavené blokace nebo z vyřešeného rozhodnutí.",
    editNote: "Doplnit spouštěč, upravit nebo povýšit na Factory jde v sekci BRAIN v hlavní navigaci.",
    mapTriggerEdit: "doplnit spouštěč",
    mapTriggerChange: "upravit spouštěč",
    mapTriggerSave: "uložit",
    mapTriggerPlaceholder: "např. hledáš, kde je která část webu",
    /// Vault workera má strop; co se za něj nevejde, worker nikdy neuvidí.
    vaultTruncated: (omitted: number, eligible: number, limit: number) =>
      `⚠ Do vaultu workera se vejde ${limit} dokumentů, projekt jich má se spouštěčem ${eligible} — ${omitted} se k workerovi nedostane. Ořez jde podle složky a názvu, ne podle důležitosti.`,
    graphNote: "uzly = poznámky, hrany = odkazy [[…]]; izolovaná tečka se nikdy nedotáhne",
    graphEmpty: "Zatím není co kreslit",
  },
  sessionDetail: {
    back: "← WORKERS",
    title: (id: string) => `Work Session ${id}`,
    workerCol: "WORKER",
    task: "TASK",
    model: "MODEL",
    tokens: "TOKENS",
    /// Primární metrika ceny běhu — tokeny jsou v sub-textu.
    weekCost: "Z TÝDENNÍHO LIMITU",
    steps: "KROKŮ",
    stepsNote: (ctx: number) => `à ~${ctx}k kontextu · baseline 63 kroků à 115k`,
    stepsUnknown: "starší runner kroky nehlásil",
    tokensNote: "→ task & projekt",
    duration: "TRVÁNÍ",
    log: "SESSION LOG // BEZ CHAIN-OF-THOUGHT",
    checkpoints: "CHECKPOINTY",
    chain: "SESSION CHAIN // TASK NEVLASTNÍ ŽÁDNÝ STROJ",
    chainNote: "task patří Departmentu — session má jen dočasný execution lease",
    handoff: "HANDOFF // PRO DALŠÍ SESSION",
    discoveries: "DISCOVERIES // MIMO SCOPE",
    blockers: "BLOKACE // NEUDĚLÁNO KVŮLI PŘEKÁŽCE",
    blockerResolved: (by: string) => `vyřešeno (${by})`,
    artifacts: "ARTIFACTS",
    footer: "Session je nezávislá na konkrétní Claude konverzaci — po výpadku workera lease expiruje a jiný stroj pokračuje z checkpointu.",
    completed: "hotovo",
    remaining: "zbývá",
    nextAction: "další krok",
  },
  connections: {
    header: "INTEGRATION MAP // AI DOSTÁVÁ JEN AKCE, KTERÉ TASK POTŘEBUJE · SECRETS NIKDY V KONTEXTU",
    connect: "+ PŘIPOJIT",
    matrix: "· MATICE →",
    connected: "● PŘIPOJENO",
    missing: "○ CHYBÍ",
    error: "⚠ CHYBA",
    legend: "legenda oprávnění:",
    legendAuto: "zelená = AI smí automaticky",
    legendApproval: "amber = vyžaduje approval",
    legendDenied: "šedá = zakázáno",
    empty: "Zatím žádné integrace — připoj první přes šablonu.",
    matrixTitle: (name: string) => `${name} // CAPABILITY MATRIX`,
    matrixNote: "autorizace per akce (spec §57) — AI dostává capabilities, nikdy credentials",
    action: "AKCE",
    mode: "REŽIM",
    note: "POZNÁMKA",
    depts: "POUŽÍVAJÍ",
    secrets: "SECRETS",
    secretsNote: "hodnoty jsou šifrované na serveru a nikdy se nezobrazují (spec §58)",
    secretName: "název (např. api-token)",
    secretValue: "hodnota",
    secretSet: "ULOŽIT SECRET",
    disconnect: "ODPOJIT",
    pickTemplate: "ŠABLONA INTEGRACE",
  },
  incidents: {
    title: "INCIDENTY",
    active: (n: number) => `${n} aktivních`,
    empty: "✓ Žádné incidenty — produkce je zdravá.",
    severity: (s: string) => `SEVERITY ${s}`,
    statusOpen: "OPEN",
    statusMonitoring: "VERIFIKACE",
    statusResolved: "RESOLVED",
    backToCommand: "← COMMAND CENTER",
    errRate: "CHYBOVOST",
    affected: "DOTČENÍ UŽIVATELÉ",
    duration: "TRVÁNÍ",
    businessRisk: "BUSINESS RISK",
    timeline: "INCIDENT TIMELINE // AUTOMATICKÝ FLOW DLE POLICY",
    blockedByYou: "BLOKOVÁNO TVÝM ROZHODNUTÍM",
    openDecision: "OTEVŘÍT DECISION →",
    reprioritization: "GLOBAL REPRIORITIZACE",
    reprioNote: (ep: number, task: string) =>
      `Hotfix task ${task} dostal EP ${ep} — přeskočil běžnou práci ostatních projektů.`,
    noHotfix: "Bez hotfix tasku (severity P2/P3 nereprioritizuje).",
    afterResolve: "PO VYŘEŠENÍ // AUTOMATICKY",
    afterResolveSteps: "→ incident RESOLVED + audit\n→ post-mortem task (Operations)\n→ Knowledge update dle post-mortemu",
    resolvedPanel: "✓ INCIDENT VYŘEŠEN",
    monitoringBtn: "FIX NASAZEN → VERIFIKACE",
    resolveBtn: "✓ OZNAČIT RESOLVED",
    resolveNote: "Co bylo příčinou a jak je vyřešeno…",
    resolveNeedsNote: "Resolve vyžaduje poznámku — vyplň pole výše.",
    postMortem: (n: number) => `Post-mortem task #${n} (Operations)`,
    hero: (n: number) => `a hlídá ${n} ${n === 1 ? "incident" : n < 5 ? "incidenty" : "incidentů"}`,
    minutes: (n: number) => (n < 60 ? `${n} min` : `${Math.floor(n / 60)} h ${n % 60} min`),
    newIncident: "+ INCIDENT",
    notFound: "Incident neexistuje.",
  },
  routine: {
    back: "← WORKERS & ROUTINES",
    active: "AKTIVNÍ",
    paused: "POZASTAVENA",
    pause: "⏸ POZASTAVIT",
    resume: "▶ AKTIVOVAT",
    runNow: "▶ SPUSTIT TEĎ",
    definition: "DEFINICE",
    schedule: "SCHEDULE",
    department: "DEPARTMENT",
    scope: "SCOPE",
    capabilities: "CAPABILITIES",
    model: "MODEL",
    instructions: "INSTRUKCE // ZÁKLAD CONTEXT PACKAGE",
    outputs: "MOŽNÉ VÝSTUPY",
    runs: "POSLEDNÍ BĚHY",
    tokenUsage: (t: string) => `token usage: ${t}`,
    runTime: "ČAS",
    runWorker: "WORKER",
    runModel: "MODEL",
    runTokens: "TOKENS",
    runOut: "VÝSTUP",
    noRuns: "Zatím žádné běhy — routine čeká na claim workera.",
    autonomy: "AUTONOMIE & ESKALACE",
    autonomyEmpty: "Bez zvláštních pravidel — platí project policies.",
    notFound: "Routine neexistuje.",
    name: "NÁZEV",
    scheduleKind: "OPAKOVÁNÍ",
    kindInterval: "interval (minuty)",
    kindDaily: "denně v čase",
    kindWeekly: "týdně",
    minutes: "MINUTY",
    time: "ČAS (HH:MM)",
    weekday: "DEN",
    weekdays: ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"],
    instructionsField: "INSTRUKCE",
    autonomyField: "AUTONOMIE & ESKALACE (popis)",
    outputsField: "POVOLENÉ VÝSTUPY",
    capabilitiesField: "POŽADOVANÉ KAPABILITY (čárkami)",
    modelField: "MODEL HINT (volitelně)",
    scopeField: "SCOPE — PROJEKTY (žádný = všechny aktivní)",
    delete: "SMAZAT ROUTINE",
  },
  audit: {
    title: "Activity / Audit",
    subtitle: "Knowledge drží aktuální pravdu, audit celou historii.",
    time: "ČAS",
    actor: "ACTOR",
    action: "AKCE",
    project: "PROJEKT",
    detail: "DETAIL / DŮVOD",
    link: "VAZBA",
    loadMore: "NAČÍST DALŠÍ",
    empty: "Žádné události.",
  },
  oauth: {
    title: "PŘIPOJENÍ KONEKTORU",
    intro: (client: string) => `${client} žádá o přístup do App Factory tvým jménem.`,
    asUser: "JMÉNEM",
    returnsTo: "VRÁTÍ SE NA",
    access: "ROZSAH",
    accessValue: "vše, co vidíš ty",
    note: "Konektor uvidí a změní jen to, na co máš právo — role a členství v projektech platí stejně jako v aplikaci. Přístup můžeš kdykoli odvolat v Nastavení → API tokeny.",
    approve: "POVOLIT",
    deny: "ZAMÍTNOUT",
  },
  settings: {
    roleHint:
      "MANAGER může zakládat projekty. CLIENT vidí jen projekty, kde je členem — nastavení projektu nikdy.",

    title: "Nastavení",
    lead: "Účty, přístupy a nastavení celé Factory.",
    appearance: "VZHLED",
    appearanceNote:
      "Motiv se ukládá k tvému účtu, ne do prohlížeče — přihlásíš se odjinud a máš ho stejný. Výchozí je tmavý.",
    factory: "FACTORY",
    factoryName: "NÁZEV FACTORY",
    timezone: "ČASOVÁ ZÓNA",
    users: "UŽIVATELÉ",
    newUser: "+ NOVÝ UŽIVATEL",
    name: "JMÉNO",
    email: "E-MAIL",
    role: "ROLE",
    password: "HESLO",
    state: "STAV",
    active: "AKTIVNÍ",
    disabled: "DEAKTIVOVÁN",
    disable: "DEAKTIVOVAT",
    created: "VYTVOŘEN",
    tokens: "API TOKENY",
    tokensNote: "osobní tokeny pro MCP konektor (Claude Desktop) a skripty",
    newToken: "+ NOVÝ TOKEN",
    tokenName: "K ČEMU TOKEN JE",
    tokenNamePlaceholder: "Claude Desktop — MacBook",
    tokenExpiry: "PLATNOST (DNY, PRÁZDNÉ = BEZ OMEZENÍ)",
    tokenIssue: "VYDAT TOKEN",
    tokenOnce: "Token vidíš jen teď — jinam se už nedostaneš. Zkopíruj ho do konfigurace konektoru.",
    tokenCopy: "KOPÍROVAT",
    tokenCopied: "✓ zkopírováno",
    tokenLastUsed: "POSLEDNÍ POUŽITÍ",
    tokenNever: "nepoužit",
    tokenExpires: "PLATNOST DO",
    tokenForever: "bez omezení",
    tokenRevoke: "ODVOLAT",
    tokenEmpty: "Žádný token — konektor zatím nemá čím se přihlásit.",
    tokenScope: "Token má tvoje práva ve Factory: uvidí přes něj jen to, co vidíš ty.",
    tokenConnector: "KONEKTOR",
  },
  hostingServers: {
    header: "HOSTING SERVERY // COOLIFY",
    note: "Servery, na kterých běží projekty — tvůj i servery jiných uživatelů. Token (read · write · deploy · read:sensitive — bez read:sensitive Coolify nevydá build logy) Factory používá ke sledování buildů a logů a vydává ho workerům jen pro session projektu na daném serveru. Zpátky se nikdy nezobrazí.",
    add: "+ PŘIDAT SERVER",
    name: "NÁZEV",
    apiUrl: "URL API",
    token: "COOLIFY API TOKEN",
    tokenHint: "Coolify → Keys & Tokens → API tokens, tým Root Team",
    tokenKeep: "prázdné = beze změny",
    noteLabel: "POZNÁMKA",
    owner: "VLASTNÍK",
    ownerSelf: "já",
    isDefault: "výchozí server pro projekty bez přiřazení",
    default: "VÝCHOZÍ",
    defaultProjects: "projekty bez přiřazení",
    noToken: "CHYBÍ",
    state: "STAV",
    ok: "✓ spojení ověřeno",
    checkOk: (v: string | null) => `✓ spojení OK${v ? ` · Coolify ${v}` : ""}`,
    checking: "OVĚŘUJI…",
    projects: "PROJEKTY",
    edit: "UPRAVIT",
    check: "OVĚŘIT",
    delete: "SMAZAT",
    deleteConfirm: (name: string) => `Smazat server ${name}? Token se z Factory nevratně odstraní.`,
    empty: "Žádný server v evidenci — Factory používá jen výchozí server z konfigurace.",
  },
  mobile: {
    decide: "ROZHODNOUT",
    decideChosen: "Rozhodnout",
    optionsCount: (n: number) => `${n} možnosti · vlastní řešení`,
    ownSolution: "vlastní řešení",
    optionsTap: "MOŽNOSTI — KLEPNI PRO VOLBU",
    recommended: "DOPORUČENO",
    blockerDecision: (project: string) => `Stojí projekt ${project} — čeká na tvé rozhodnutí`,
    blockerIncident: (project: string, label: string) => `${label} na projektu ${project} — incident běží`,
    // Pátá záložka se vešla díky užšímu popisku v tab baru (10px/0.06em místo
    // 11px/0.14em) — plná slova zůstávají, jen se netlačí na sebe.
    tabs: {
      home: "PŘEHLED",
      decisions: "ROZHODNUTÍ",
      humanTasks: "ÚKOLY",
      jarvis: "JARVIS",
      menu: "MENU",
    },
    back: "← ZPĚT",
    menuTitle: "NAVIGACE // CELÁ FACTORY",
    menuSettings: "NASTAVENÍ",
    menuClose: "ZAVŘÍT",
    heroRunning: "Factory běží.",
    heroWaiting: "Čekají",
    heroDecisions: (n: number) => `${n} rozhodnutí`,
    heroWatching: ", hlídám",
    heroIncidents: (n: number) => `${n} ${n === 1 ? "incident" : n < 5 ? "incidenty" : "incidentů"}`,
    heroCalm: "Nic nečeká — všechno běží samo.",
    kpiSessions: "SESSIONS",
    kpiDecisions: "ROZHODNUTÍ",
    kpiIncidents: "INCIDENTY",
    projects: "PROJEKTY // PODLE ATTENTION",
    plan: "EXECUTION PLAN // NOW → NEXT",
    planIdle: "Žádná session neběží — workery čekají na claim.",
    planNext: "next:",
    digest: "PŘES NOC + DNES",
    digestDone: (n: number) => `${n} tasků dokončeno dnes`,
    digestNothing: "Zatím klid — Factory se ozve push notifikací.",
    pushCritical: "PUSH · KRITICKÉ ROZHODNUTÍ",
    pushIncident: "PUSH · INCIDENT",
    pushOpen: "otevřít →",
    enableNotifications: "🔔 POVOLIT NOTIFIKACE",
    notificationsOn: "🔔 notifikace zapnuty",
    inboxTitle: "Decision Inbox",
    inboxWaiting: (n: number) => `${n} čeká`,
    writeFinding: "NAPSAT NÁLEZ",
    pushHumanTask: "PUSH · ÚKOL PRO TEBE",
    approve: "✓ SCHVÁLIT",
    allDecided: "✓ Vše rozhodnuto",
    allDecidedNote: (n: number) =>
      n > 0
        ? `Odblokoval jsi ${n} ${n === 1 ? "task" : n < 5 ? "tasky" : "tasků"}.\nFactory pokračuje — ozvu se push notifikací.`
        : "Factory pokračuje — ozvu se push notifikací.",
    jarvisTapIdle: "◉ KLEPNI A MLUV — MIKROFON VYPNUT",
    jarvisListening: "● POSLOUCHÁM · MIKROFON AKTIVNÍ",
    jarvisThinking: "● PŘEMÝŠLÍM…",
    jarvisTapNote: "Klepni na orb a mluv.\nMikrofon se aktivuje jen na tvůj pokyn.",
    jarvisTypeNote: "Hlas není nakonfigurovaný — piš dole.",
  },
  jarvis: {
    name: "JARVIS",
    offline: "JARVIS OFFLINE",
    offlineNote: "Nastav OPENROUTER_API_KEY a Jarvis ožije.",
    notConfigured: "○ JARVIS OFFLINE · CHYBÍ OPENROUTER_API_KEY",
    intro: "Poslouchám. Zeptej se na cokoliv — UI se přizpůsobí tomu, o čem mluvíme.",
    offlineIntro: "Jarvis potřebuje OPENROUTER_API_KEY (viz docs/DEPLOYMENT.md). Bez něj umí jen mlčet — a to umí skvěle.",
    suggestions: [
      "Co je nového?",
      "Jak je na tom Rezervo?",
      "Co ode mě potřebuješ?",
      "Dej projektu Rezervo nejvyšší prioritu.",
    ],
    placeholder: "napiš, nebo drž 🎙 a mluv…",
    thinking: "přemýšlím…",
    impact: "DOPAD PROVEDENÝ FACTORY",
    end: "UKONČIT",
    voiceActive: "VOICE MODE · ODPOVÍDÁM HLASEM",
    listening: "VOICE MODE · POSLOUCHÁM…",
    voiceToggle: "Hlasový vstup (ElevenLabs)",
    voiceOutputOn: "Hlasové odpovědi zapnuté — klepni pro přepnutí na text",
    voiceOutputOff: "Hlasové odpovědi vypnuté — klepni pro zapnutí hlasu",
    playBlocked: "▶ PŘEHRÁT ODPOVĚĎ",
    playBlockedNote: "prohlížeč zablokoval automatické přehrání",
    sttEmpty: "Nerozuměl jsem — zkus to znovu.",
    micDenied: "Mikrofon není povolený v prohlížeči.",
    showing: "◉ JARVIS UKAZUJE",
  },
  palette: {
    placeholder: "Přejít na… / provést akci…",
    navigate: "NAVIGACE",
    project: "PROJEKT",
    action: "AKCE",
    empty: "Nic nenalezeno.",
  },
  notifications: {
    title: "NOTIFIKACE",
    markRead: "označit přečtené",
    empty: "Žádné nové notifikace.",
    note: "kritické notifikace půjdou zároveň push na mobil (Fáze 9)",
  },
  phases: {
    plan: {
      title: "Execution Plan",
      note: "Globální fronta podle Effective Priority a NOW / NEXT / THEN pohled na workery přijdou ve Fázi 3–4.",
    },
    decisions: {
      title: "Decision Inbox",
      note: "Rozhodnutí, AI doporučení, alternativy a dopady přijdou ve Fázi 3.",
    },
    goals: {
      title: "Goals & Roadmap",
      note: "Company → Project goals, iniciativy a traceability přijdou ve Fázi 3.",
    },
    workers: {
      title: "Workers",
      note: "Worker Registry, Claude Desktop Connector, sessions a Live Factory View přijdou ve Fázi 4.",
    },
    departments: {
      title: "Oddělení",
      note: "Globální registr oddělení a Workflow Builder přijdou ve Fázi 3.",
    },
  },
  login: {
    hint: "Výchozí účet vznikl seedem — přihlašovací údaje jsou v .env (SEED_OWNER_*).",
  },
  errors: {
    unauthorized: "Neplatný e-mail nebo heslo",
  },
} as const;

export type Messages = typeof cs;
